import { clamp } from 'es-toolkit/compat';
import React from 'react';

import { jobDrawerWidth as defaultJobDrawerWidth } from 'components/atoms/FlexContainers/FlexContainers';
import { getStoredPreferenceValue, setStoredPreferenceValue } from 'utils/userPreferences';

export const jobDrawerMinWidth = 320;
export const jobDrawerMaxWidth = 720;
export const jobDrawerWidthStorageKey = 'jobsDrawerWidth';

const jobDrawerWidthChangeEventName = 'jobs-drawer-width-change';
const jobDrawerResizeStep = 24;
const jobDrawerViewportGutter = 48;
const jobDrawerMinMainAreaWidth = 640;
const jobDrawerFullWidthBreakpoint = 768;

const getViewportWidth = () => (typeof window === 'undefined' ? defaultJobDrawerWidth : window.innerWidth);

export const getJobDrawerWidthBounds = (viewportWidth: number) => {
  const normalizedViewportWidth =
    Number.isFinite(viewportWidth) && viewportWidth > 0 ? viewportWidth : defaultJobDrawerWidth;

  if (normalizedViewportWidth <= jobDrawerMinWidth) {
    return { minWidth: normalizedViewportWidth, maxWidth: normalizedViewportWidth };
  }

  const maxWidth =
    normalizedViewportWidth <= jobDrawerFullWidthBreakpoint
      ? normalizedViewportWidth - jobDrawerViewportGutter
      : Math.min(
          jobDrawerMaxWidth,
          normalizedViewportWidth - jobDrawerViewportGutter,
          Math.max(jobDrawerMinWidth, normalizedViewportWidth - jobDrawerMinMainAreaWidth)
        );

  return { minWidth: Math.min(jobDrawerMinWidth, maxWidth), maxWidth };
};

export const normalizeJobDrawerWidth = (width: number, viewportWidth: number) => {
  const { minWidth, maxWidth } = getJobDrawerWidthBounds(viewportWidth);
  const fallbackWidth = clamp(defaultJobDrawerWidth, minWidth, maxWidth);

  return Number.isFinite(width) ? clamp(Math.round(width), minWidth, maxWidth) : fallbackWidth;
};

const getStoredJobDrawerWidth = () => {
  if (typeof window === 'undefined') {
    return defaultJobDrawerWidth;
  }

  try {
    const storedPreference = getStoredPreferenceValue(jobDrawerWidthStorageKey);
    const storedWidth = storedPreference === null ? Number.NaN : Number(storedPreference);
    return normalizeJobDrawerWidth(storedWidth, getViewportWidth());
  } catch {
    return normalizeJobDrawerWidth(Number.NaN, getViewportWidth());
  }
};

export const useJobDrawerWidth = () => {
  const [drawerWidth, setDrawerWidth] = React.useState<number>(() => getStoredJobDrawerWidth());

  React.useEffect(() => {
    const syncDrawerWidth = () => setDrawerWidth(getStoredJobDrawerWidth());
    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== jobDrawerWidthStorageKey) {
        return;
      }
      syncDrawerWidth();
    };

    window.addEventListener(jobDrawerWidthChangeEventName, syncDrawerWidth);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('resize', syncDrawerWidth);
    syncDrawerWidth();

    return () => {
      window.removeEventListener(jobDrawerWidthChangeEventName, syncDrawerWidth);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('resize', syncDrawerWidth);
    };
  }, []);

  const setJobDrawerWidth = React.useCallback((nextWidth: number) => {
    const normalizedWidth = normalizeJobDrawerWidth(nextWidth, getViewportWidth());
    setDrawerWidth(normalizedWidth);
    try {
      setStoredPreferenceValue(jobDrawerWidthStorageKey, normalizedWidth.toString());
    } catch {
      // Keep the in-memory width if browser storage is unavailable.
    }
    window.dispatchEvent(new Event(jobDrawerWidthChangeEventName));
  }, []);

  return { jobDrawerWidth: drawerWidth, setJobDrawerWidth };
};

type JobDrawerResizeEdge = 'left' | 'right';

export const useJobDrawerResizer = ({
  drawerWidth,
  edge,
  setJobDrawerWidth,
}: {
  drawerWidth: number;
  edge: JobDrawerResizeEdge;
  setJobDrawerWidth: (width: number) => void;
}) => {
  const [isResizingDrawer, setIsResizingDrawer] = React.useState(false);
  const resizeSessionRef = React.useRef<{ startX: number; startWidth: number } | null>(null);
  const drawerWidthRef = React.useRef(drawerWidth);
  const setDrawerWidthRef = React.useRef(setJobDrawerWidth);
  drawerWidthRef.current = drawerWidth;
  setDrawerWidthRef.current = setJobDrawerWidth;

  const viewportWidth = getViewportWidth();
  const { minWidth, maxWidth } = getJobDrawerWidthBounds(viewportWidth);

  React.useEffect(() => {
    if (!isResizingDrawer) {
      return undefined;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const session = resizeSessionRef.current;
      if (!session) {
        return;
      }

      const direction = edge === 'right' ? 1 : -1;
      setDrawerWidthRef.current(session.startWidth + direction * (event.clientX - session.startX));
    };
    const handlePointerEnd = () => {
      resizeSessionRef.current = null;
      setIsResizingDrawer(false);
    };

    document.body.style.setProperty('cursor', 'col-resize');
    document.body.style.setProperty('user-select', 'none');
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);

    return () => {
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [edge, isResizingDrawer]);

  const handleResizePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    resizeSessionRef.current = { startX: event.clientX, startWidth: drawerWidthRef.current };
    setIsResizingDrawer(true);
  };

  const handleResizeHandleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const expandKey = edge === 'right' ? 'ArrowRight' : 'ArrowLeft';
    const shrinkKey = edge === 'right' ? 'ArrowLeft' : 'ArrowRight';

    if (event.key === expandKey) {
      event.preventDefault();
      setJobDrawerWidth(drawerWidthRef.current + jobDrawerResizeStep);
    } else if (event.key === shrinkKey) {
      event.preventDefault();
      setJobDrawerWidth(drawerWidthRef.current - jobDrawerResizeStep);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setJobDrawerWidth(minWidth);
    } else if (event.key === 'End') {
      event.preventDefault();
      setJobDrawerWidth(maxWidth);
    }
  };

  return {
    isResizingDrawer,
    minDrawerWidth: minWidth,
    maxDrawerWidth: maxWidth,
    handleResizePointerDown,
    handleResizeHandleKeyDown,
  };
};
